import React from 'react';
import { Icon } from 'components/Icon';
import { faArrowDownShortWide, faArrowDownWideShort, faDownLong, faUpLong } from '@fortawesome/free-solid-svg-icons';
import { Header as HeaderType } from 'types/Table';
import { ROW_DETAILS_TABLE_TOGGLE } from 'helpers/constants/table';

interface HeaderProps {
    results?: unknown[];
    headers: HeaderType[];
    hasSort?: boolean;
    sortColumn?: string;
    triggerSearchChange: (params: {
        sortColumn: HeaderType,
        sortAsc: boolean
    }) => void;
    sortAsc?: boolean;
    sortableColumns?: number[];
}

interface SortingProps {
    column: HeaderType;
    i: number;
}

export const Header = ({
    results,
    headers,
    hasSort,
    sortColumn,
    triggerSearchChange,
    sortAsc,
    sortableColumns,
}: HeaderProps) => {
    if (!results) {
        return null;
    }

    const sortColumnObj = headers.find(h => {
        const { column_map, apiMap = false } = h;
        const columnName = apiMap || column_map;
        return columnName === sortColumn;
    });

    const renderEmptyHeader = (i: number) => (
        <th key={`th-empty-${i}`} className="empty-header" />
    );

    const renderStaticHeader = (column: HeaderType, i: number) => (
        <th key={`th-${i}`} className={column.className}>
            <span>{column.body}</span>
        </th>
    );

    const renderSortableHeader = ({
        column,
        i,
        onClick,
        children,
    }: {
        column: HeaderType;
        i: number;
        onClick: () => void;
        children: React.ReactNode;
    }) => (
        <th
            key={`th-${i}`}
            className={`sort-arrow ${column.className || ''}`}
            onClick={onClick}
        >
            <span>{column.body} </span>
            {children}
        </th>
    );

    const Sorting = ({ column, i }: SortingProps) => {
        if (!column || column.body === false) {
            return renderEmptyHeader(i);
        }

        if (typeof column === 'string' && column === ROW_DETAILS_TABLE_TOGGLE) {
            return renderEmptyHeader(i);
        }

        const trigger = (direction: 'asc' | 'desc') => {
            triggerSearchChange({
                sortColumn: column,
                sortAsc: direction === 'asc',
            });
        };

        const columnName = column.body;
        const isCurrentSortColumn = columnName === sortColumnObj?.body || columnName === sortColumnObj;
        const isSortableColumn = !sortableColumns || sortableColumns.includes(i);

        if (!hasSort || !isSortableColumn) {
            return renderStaticHeader(column, i);
        }

        if (!isCurrentSortColumn) {
            return renderSortableHeader({
                column,
                i,
                onClick: () => trigger('asc'),
                children: (
                    <>
                        &nbsp;
                        <span className="sort-arrow sort">
                            <Icon icon={faUpLong} size="sm" />
                            <Icon icon={faDownLong} size="sm" />
                        </span>
                    </>
                ),
            });
        }

        if (sortAsc) {
            return renderSortableHeader({
                column,
                i,
                onClick: () => trigger('desc'),
                children: <Icon icon={faArrowDownShortWide} size="1x" onClick={() => trigger('desc')} />,
            });
        }

        return renderSortableHeader({
            column,
            i,
            onClick: () => trigger('asc'),
            children: <Icon icon={faArrowDownWideShort} size="1x" onClick={() => trigger('asc')} />,
        });
    };

    return (
        <thead>
            <tr>
                {headers.map((item, i) => (
                    <Sorting key={`sorting-${i}`} column={item} i={i} />
                ))}
            </tr>
        </thead>
    );
};